import { useCallback, useMemo } from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';

import { getPortalRoutePath } from './Routes';

type PushInput =
  | string
  | {
      pathname: string;
      hash?: string;
    };

function getRelativeHref(href: string) {
  if (typeof window === 'undefined') {
    return href;
  }

  return href.replace(window.location.origin, '');
}

function normaliseHash(hash?: string) {
  if (!hash) {
    return undefined;
  }

  return hash.startsWith('#') ? hash.slice(1) : hash;
}

export function useRouter() {
  const navigate = useNavigate();
  const routerState = useRouterState();
  const location = routerState.location;

  const back = useCallback(() => {
    window.history.back();
  }, []);

  const reload = useCallback(() => {
    window.location.reload();
  }, []);

  const push = useCallback(
    (input: PushInput) => {
      const nextLocation =
        typeof input === 'string' ? { pathname: input } : input;
      const pathname = getPortalRoutePath(nextLocation.pathname);
      const hash = normaliseHash(nextLocation.hash);

      if (pathname) {
        return navigate({
          to: pathname,
          hash,
        });
      }

      const target = new URL(nextLocation.pathname, window.location.origin);
      if (hash) {
        target.hash = hash;
      }

      window.location.href = target.toString();
    },
    [navigate]
  );

  const query = useMemo(() => {
    return location.search as Record<string, string | string[] | undefined>;
  }, [location.search]);

  const asPath = useMemo(() => {
    return getRelativeHref(location.href);
  }, [location.href]);

  return useMemo(() => {
    return {
      ...location,
      pathname: location.pathname,
      asPath,
      query,
      back,
      reload,
      push,
    };
  }, [asPath, back, location, push, query, reload]);
}
